import { useQuery } from '@apollo/client';
import { Button, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ManageDataGrid } from '~/components/data-grid';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { useAppContext } from '~/contexts';
import type { ContentCondition } from '~/generated/graphql';
import { ContentFolderContentTagsDocument } from '../ContentFolderView.generated';
import { useTagUtils } from '../lib';

export interface ContentTagsDialogProps {
  condition: ContentCondition;
  contentFolderId: number;
  onClose: () => void;
  open: boolean;
  page: number;
  perPage: number;
}

export const ContentTagsDialog = ({
  condition,
  contentFolderId,
  onClose,
  open,
  page,
  perPage,
}: ContentTagsDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data } = useQuery(ContentFolderContentTagsDocument, {
    variables: {
      networkId: currentNetwork.id,
    },
    fetchPolicy: 'cache-first',
  });

  const {
    cellModesModel,
    columns,
    confirmDeleteProps,
    handleCellEditStart,
    handleCellEditStop,
    handleCellFocus,
    handleCellKeyDown,
    processRowUpdate,
    rows,
    selectedCellParams,
    setCellModesModel,
  } = useTagUtils(data?.network?.contentTags || [], { condition, contentFolderId, page, perPage });

  const selectedRow = useMemo(
    () => rows.find((x) => x.id === selectedCellParams?.id),
    [rows, selectedCellParams],
  );

  const itemText =
    selectedRow?.uses === 1 ? t('common.tags.delete_tag_item') : t('common.tags.delete_tag_items');

  return (
    <Dialog onClose={onClose} fullWidth maxWidth="sm" open={open}>
      <DialogTitle onClose={onClose}>{t('common.tags.manage_tags')}</DialogTitle>
      <DialogContent>
        {rows.length > 0 ? (
          <ManageDataGrid
            cellModesModel={cellModesModel}
            columns={columns}
            disableColumnMenu
            disableRowSelectionOnClick
            onCellEditStart={handleCellEditStart}
            onCellEditStop={handleCellEditStop}
            onCellKeyDown={handleCellKeyDown}
            onCellModesModelChange={setCellModesModel}
            processRowUpdate={processRowUpdate}
            rows={rows}
            slots={{
              columnResizeIcon: () => null,
            }}
            slotProps={{
              cell: {
                onFocus: handleCellFocus,
              },
            }}
          />
        ) : (
          <Typography variant="subtitle2" sx={{ paddingY: 2 }}>
            {t('common.tags.no_tags')}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('common.dialogs.close')}
        </Button>
      </DialogActions>

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.tags.delete_tag_confirm')}
        deleteConfirm
        prompt={
          <Trans
            i18nKey="common.tags.delete_tag_prompt_message_plural"
            values={{
              tagName: selectedRow?.name,
              count: selectedRow?.uses,
              deviceWord: itemText,
            }}
            components={{ b: <strong /> }}
          />
        }
        title={t('common.tags.delete_tag_title')}
      />
    </Dialog>
  );
};
